import style from "./TimeTable.module.css";
import ClassNames from "../../util/ClassNames";
import { Availability, Course, Store } from "../../api/ApiData";
import Time from "../../util/Time";
import Settings from "../../util/Settings";
import { Box, Link, Stack } from "@mui/material";
import { ColorHexCodes } from "../../util/Color";
import { getStoreBusinessHours, getSlotDuration, createDaysLabelItem } from "./TimeTableUtils";

type StaticTimeTableArgs = {
  page: number; 
  store: Store; 
  availability: Availability; 
  cellLink: string; 
  rowCount: number
};

export function StaticTimeTable({ page, store, availability, cellLink, rowCount }: StaticTimeTableArgs) {
  const businessHours = getStoreBusinessHours(store);
  const slotDuration = getSlotDuration(store);
  const earliestReservableTime = Math.min(...availability.map((_) => new Time(_).getTimeFromStartOfDay()));

  const slots: number[] = [];
  for (let slot = businessHours.openAt; slot < businessHours.closeAt; slot += slotDuration) {
    slots.push(slot);
  }
  while (rowCount < slots.length) {
    if (slots[0] < earliestReservableTime) {
      slots.shift();
    } else {
      slots.splice(rowCount);
      break;
    }
  }

  // 列のラベル(日付)を作成する
  const daysLabelItem = createDaysLabelItem(page);

  // 行のラベル(時間)を作成する
  const timeLabels = createStaticTimeLabels(store, slots);

  // 予約セルを作成する
  const dataCells = createStaticDataCells(store, slots, availability, page, cellLink);

  return (
    <Stack sx={{ textAlign: "center" }} className={ClassNames(style.cellWrapper)}>
      <Stack style={{ flexDirection: "row", background: "white" }}>
        <Box className={ClassNames(style.grow, style.cell, style.timeColumn)}>日時</Box>
        {daysLabelItem}
      </Stack>
      <Stack style={{ flexDirection: "row" }}>
        <Stack className={ClassNames(style.grow, style.timeColumn)}>{timeLabels}</Stack>
        {dataCells}
      </Stack>
    </Stack>
  );
}

function createStaticTimeLabels(store: Store, slots: number[]) {
  const businessHours = getStoreBusinessHours(store);
  const slotDuration = getSlotDuration(store);

  return slots.map((slot, i) => {
    const totalMinutes = slot / Time.AMinute;
    const hour = Math.floor(totalMinutes / 60);
    const min = (totalMinutes % 60).toString().padStart(2, "0");
    return (
      <Box key={i} className={ClassNames(style.cell, style.timeColumn)} style={{ height: "45px" }}>
        {i === slots.length - 1 && slot < businessHours.closeAt - slotDuration ? `${hour}:${min}以降` : `${hour}:${min}`}
      </Box>
    );
  });
}

function createStaticDataCells(store: Store, slots: number[], availability: Availability, page: number, cellLink: string) {
  const businessHours = getStoreBusinessHours(store);
  const slotDuration = getSlotDuration(store);

  return Array(Settings.DateUnitCount)
    .fill(0)
    .map((_, dateOffset) => {
      const date = Time.Now().Today() + (dateOffset + page * Settings.DateUnitCount) * Time.ADay;
      const reservables = availability.filter((t) => t < date + Time.ADay);
      const column = slots.map((slot, slotIndex) => {
        let slotSymbol = "×";
        let symbolColor = "";
        let classNameString = ClassNames(style.cell, style.unavailable, style.availablityCell);
        let clickable = false;
        let link = `${cellLink}&startAt=${date + slot}`;

        // 表示する最後のユニットかどうか
        if (slotIndex === slots.length - 1) {
          // これ以降の空きユニットを探して、空きユニット数が合計ユニット数の50%以上である場合に◎, 50%未満かつ0%を超える場合に△, 0%の場合に×を表示する
          const unitCount = Math.ceil((businessHours.closeAt - slot) / slotDuration);
          const avaliableUnitCount = availability.filter((t) => t - date >= slot && t - date < Time.ADay).length;
          link = cellLink;

          if (avaliableUnitCount >= unitCount / 2) {
            slotSymbol = "◎";
            symbolColor = `#${ColorHexCodes.RequiredRed}`;
            classNameString = ClassNames(style.cell, style.clickable, style.availablityCell);
            clickable = true;
          } else if (avaliableUnitCount !== 0) {
            slotSymbol = "△";
            symbolColor = `#${ColorHexCodes.MainColorGreen}`;
            classNameString = ClassNames(style.cell, style.clickable, style.availablityCell);
            clickable = true;
          }
        } else {
          if (reservables.includes(date + slot)) {
            slotSymbol = "◎";
            symbolColor = `#${ColorHexCodes.RequiredRed}`;
            classNameString = ClassNames(style.cell, style.clickable, style.availablityCell);
            clickable = true;
          }
        }

        if (clickable) {
          return (
            <Link key={slot} href={link} style={{ color: `${symbolColor}`, textDecoration: "none" }} className={classNameString}>
              {slotSymbol}
            </Link>
          );
        } else {
          return (
            <Box key={slot} style={{ color: `${symbolColor}` }} className={classNameString}>
              {slotSymbol}
            </Box>
          );
        }
      });
      return (
        <Stack key={dateOffset} className={style.grow}>
          {column}
        </Stack>
      );
    });
}